"use client";

import { resetPageLoader } from "@/components/page-loader";
import { resetOnboardingCheck } from "@/components/layout/onboarding-guard";
import { parseAppRelativeUrl, serializeAppUrl } from "@/lib/invitation-links";
import { clearIdentity } from "@/lib/observability/client";

const LOGIN_PATH = "/login";
const SIGNED_OUT_PARAM = "signed_out";
const LOGIN_ERROR_PARAM = "error";

const FORCED_SIGN_OUT_NOTICES = {
  another_device: {
    title: "Signed out",
    description:
      "You were signed out because your account was used on another device.",
  },
  session_ended: {
    title: "Session ended",
    description: "Please sign in again.",
  },
} as const;

const LOGIN_ERROR_NOTICES = {
  login_state: {
    title: "Sign in expired",
    description:
      "Please try signing in again. This can happen if the sign-in link expired or another sign-in tab was opened.",
  },
} as const;

export type ForcedSignOutReason = keyof typeof FORCED_SIGN_OUT_NOTICES;
type LoginErrorReason = keyof typeof LOGIN_ERROR_NOTICES;

interface SessionNotice {
  title: string;
  description: string;
}

export interface ExplicitLogoutOptions {
  returnTo?: string;
}

function appRelativePath(path: string | undefined): string {
  const parsedUrl = parseAppRelativeUrl(path);
  return parsedUrl ? serializeAppUrl(parsedUrl) : LOGIN_PATH;
}

function navigate(url: string) {
  if (typeof window === "undefined") return;
  window.location.assign(url);
}

function loginUrlForForcedSignOut(reason?: ForcedSignOutReason) {
  if (!reason) return LOGIN_PATH;
  const params = new URLSearchParams({ [SIGNED_OUT_PARAM]: reason });
  return `${LOGIN_PATH}?${params.toString()}`;
}

function isForcedSignOutReason(reason: string): reason is ForcedSignOutReason {
  return Object.prototype.hasOwnProperty.call(FORCED_SIGN_OUT_NOTICES, reason);
}

function isLoginErrorReason(error: string): error is LoginErrorReason {
  return Object.prototype.hasOwnProperty.call(LOGIN_ERROR_NOTICES, error);
}

export function resetClientSessionState() {
  // Auth cookies are HttpOnly and cleared by the backend's Set-Cookie response
  // on logout / forced-signout flows. The client only needs to reset its own
  // in-memory state.
  resetPageLoader();
  resetOnboardingCheck();
  clearIdentity();
}

export function getForcedSignOutNotice(
  reason: string | null | undefined,
): SessionNotice | null {
  if (!reason) return null;

  if (!isForcedSignOutReason(reason)) return null;

  return FORCED_SIGN_OUT_NOTICES[reason];
}

export function getLoginErrorNotice(
  error: string | null | undefined,
): SessionNotice | null {
  if (!error) return null;

  if (!isLoginErrorReason(error)) return null;

  return LOGIN_ERROR_NOTICES[error];
}

function clearCurrentUrlParam(paramName: string) {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  if (!url.searchParams.has(paramName)) return;

  url.searchParams.delete(paramName);
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

export function clearSignedOutParamFromCurrentUrl() {
  clearCurrentUrlParam(SIGNED_OUT_PARAM);
}

export function clearLoginErrorParamFromCurrentUrl() {
  clearCurrentUrlParam(LOGIN_ERROR_PARAM);
}

export function handleForcedSignOut(reason?: ForcedSignOutReason) {
  resetClientSessionState();
  navigate(loginUrlForForcedSignOut(reason));
}

export function handleExplicitLogout(
  logoutUrl?: string | null,
  options: ExplicitLogoutOptions = {},
) {
  resetClientSessionState();
  if (logoutUrl) {
    navigate(logoutUrl);
    return;
  }

  navigate(appRelativePath(options.returnTo));
}
